/**
 * Who may see and edit an org-defined employee custom field (EMP-7, Keka's "Manage Field
 * Permissions"). Pure and shared: the API filters values and decides instant-vs-approval with it,
 * and the web labels the same fields with it, so the form can never promise something the server
 * won't do.
 *
 * HR (whoever may update the employee) always sees and edits every custom field. Everyone else
 * sees a field only when its settings say so — the default is HR-only, the safe choice for the
 * medical, visa and similar data organisations put in custom fields. The per-role
 * hidden/masked/view/edit matrix is a later, larger task (RP-9); these three settings are its
 * subset and move into it without data changes.
 */

/** What the employee can do with their own value. */
export const CUSTOM_FIELD_SELF_ACCESS = ['hidden', 'view', 'edit'] as const;
export type CustomFieldSelfAccess = (typeof CUSTOM_FIELD_SELF_ACCESS)[number];

/** When `selfAccess` is `edit`: applied at once, or held for HR (the Profile Changes queue). */
export const CUSTOM_FIELD_EDIT_APPROVAL = ['none', 'hr'] as const;
export type CustomFieldEditApproval = (typeof CUSTOM_FIELD_EDIT_APPROVAL)[number];

/** What the employee's reporting (direct) manager can do. */
export const CUSTOM_FIELD_MANAGER_ACCESS = ['hidden', 'view'] as const;
export type CustomFieldManagerAccess = (typeof CUSTOM_FIELD_MANAGER_ACCESS)[number];

export const CUSTOM_FIELD_ACCESS_DEFAULTS = {
  selfAccess: 'hidden',
  selfEditApproval: 'hr',
  managerAccess: 'hidden',
} as const satisfies {
  selfAccess: CustomFieldSelfAccess;
  selfEditApproval: CustomFieldEditApproval;
  managerAccess: CustomFieldManagerAccess;
};

/**
 * Who is looking at an employee's record, as far as custom fields care. Resolved in that order:
 * someone who may update the employee is `hr` even when it's their own record or report.
 */
export type CustomFieldViewer = 'hr' | 'self' | 'manager' | 'other';

export interface CustomFieldAccess {
  selfAccess: CustomFieldSelfAccess;
  managerAccess: CustomFieldManagerAccess;
}

export function customFieldVisibleTo(field: CustomFieldAccess, viewer: CustomFieldViewer): boolean {
  switch (viewer) {
    case 'hr':
      return true;
    case 'self':
      return field.selfAccess !== 'hidden';
    case 'manager':
      return field.managerAccess === 'view';
    case 'other':
      return false;
  }
}

/** `values` with only the keys this viewer may see; a key with no definition is dropped. */
export function filterCustomFieldValues<V>(
  values: Readonly<Record<string, V>>,
  definitions: ReadonlyMap<string, CustomFieldAccess>,
  viewer: CustomFieldViewer,
): Record<string, V> {
  if (viewer === 'hr') return { ...values };
  return Object.fromEntries(
    Object.entries(values).filter(([key]) => {
      const definition = definitions.get(key);
      return definition !== undefined && customFieldVisibleTo(definition, viewer);
    }),
  );
}

/**
 * A custom field's change inside a ProfileUpdateRequest is keyed `custom.<fieldKey>` — a
 * namespace no built-in key (`phone`, `bankName`, …) or family/education key (`family.*`,
 * `education.*`) can collide with, since field keys never contain a dot. Approval and sealing
 * check for it FIRST, so a custom field labelled "Office City" or "Company" is never mistaken for
 * a built-in field by the older label-matching fallbacks.
 */
export const CUSTOM_FIELD_CHANGE_PREFIX = 'custom.';

/** Every self-service custom field change lands in one request under this category. */
export const CUSTOM_FIELDS_REQUEST_CATEGORY = 'Custom Fields';

export function customFieldChangeKey(fieldKey: string): string {
  return `${CUSTOM_FIELD_CHANGE_PREFIX}${fieldKey}`;
}

export function isCustomFieldChangeKey(key: string | undefined): key is string {
  return key !== undefined && key.startsWith(CUSTOM_FIELD_CHANGE_PREFIX);
}

/** `custom.blood_group` → `blood_group`. */
export function fieldKeyOfCustomChange(changeKey: string): string {
  return changeKey.slice(CUSTOM_FIELD_CHANGE_PREFIX.length);
}
